import { memo, ReactNode, useCallback, useMemo } from 'react'
import { optionTradeDataType } from '../../../typings/options.typings'
import { Box, useTheme } from '@mui/material'
import StrikeTooltip from '../../../../components/StrikeTooltip/StrikeTooltip'
import useStyles from '../styles'
import { useSelector } from 'react-redux'
import { selectOptionChainTableView } from '../../../../selectors/common'
import { OptionChainTableView } from '../../fnoHeader/optionsChainSettingsDialog'
import { WorkOutlineRounded } from '@mui/icons-material'
import { NonInteractiveTooltip } from '../../../../components/custom'
import { getAtmStrikeTextSx, getStrikeTextHighlightSx } from './strikePopoverStyles'
import { openOptionChainPopover, useIsOptionChainPopoverActive } from './OptionChainPopoverContext'
import { useOptionChainStrikePositionMap } from './OptionChainPositionContext'

const CustomRenderStrike = ({ obj, i, record, priceMultiplier, strikeRoundingRequired, actionBtns, atmStrike }: {
    obj: { strike: number; pcr: number, pcrStrength: number }
    record: optionTradeDataType
    i: number
    priceMultiplier: number
    strikeRoundingRequired: boolean
    actionBtns?: ReactNode
    atmStrike?: number
}) => {
    const theme = useTheme()
    const { classes, cx } = useStyles()
    const strikeToPositionMap = useOptionChainStrikePositionMap()
    const positionData = strikeToPositionMap?.[obj.strike]
    const selectedOptionChainTableView = useSelector(selectOptionChainTableView)
    const popoverId = `${record.key}-strike-${obj.strike}`
    const isPopoverOpen = useIsOptionChainPopoverActive(popoverId)

    const sdTitleLeft = useMemo(() => {
        return 2
    }, [selectedOptionChainTableView])

    const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
        openOptionChainPopover(
            popoverId,
            event.currentTarget,
            <StrikeTooltip
                keyValueType={false}
                record={record}
                roundStrike={strikeRoundingRequired}
                partial="none"
            />
        )
    }, [popoverId, record, strikeRoundingRequired])
    const isAtmStrike = atmStrike != null && obj.strike === atmStrike
    const strikeTextHighlightSx = useMemo(
        () => getStrikeTextHighlightSx(theme, isPopoverOpen),
        [theme, isPopoverOpen]
    )
    const atmStrikeTextSx = useMemo(
        () => (isAtmStrike ? getAtmStrikeTextSx(theme) : {}),
        [isAtmStrike, theme]
    )
    const strikeBaseBg =
        selectedOptionChainTableView === OptionChainTableView.NORMAL
            ? 'transparent'
            : theme.palette.background.paper

    return (
        <Box
            onClick={handleClick}
            className={cx('table-detail', 'strike-column-inner', 'font-feature-num')}
            data-event={'Option Chain Strike Clicked'}
            data-event-strike={obj.strike}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.25,
                cursor: 'default',
                backgroundColor: strikeBaseBg,
            }}
        >
            <Box sx={{ width: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {
                    selectedOptionChainTableView === OptionChainTableView.NORMAL && positionData?.call?.quantity ?
                        <NonInteractiveTooltip title={`${positionData.call.type === 'buy' ? 'Buy' : 'Sell'} ${positionData.call.quantity}`}>
                            <WorkOutlineRounded sx={{ fontSize: '12px', display: 'block', color: theme => positionData.call.type === 'buy' ? theme.palette.buy.main : theme.palette.sell.main }} />
                        </NonInteractiveTooltip> : null
                }
            </Box>
            {actionBtns && (
                <div
                    style={{ position: 'absolute', top: 0, zIndex: 20, left: positionData ? "220px" : "160px" }}
                    onClick={(event) => event.stopPropagation()}
                >
                    {actionBtns}
                </div>
            )}
            <Box
                sx={{
                    zIndex: 10,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    minWidth: 0,
                    borderRadius: '4px',
                    px: 0.75,
                    py: 0.5,
                    mx: 0.25,
                    lineHeight: 1,
                    cursor: 'default',
                    ...atmStrikeTextSx,
                    ...strikeTextHighlightSx,
                }}
            >
                {strikeRoundingRequired ? (Number(obj.strike) / (priceMultiplier ?? 100)).toFixed(0) : (Number(obj.strike) / (priceMultiplier ?? 100))?.toFixed(2)}
            </Box>
            <Box sx={{ width: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {
                    (selectedOptionChainTableView === OptionChainTableView.NORMAL ||
                        selectedOptionChainTableView === OptionChainTableView.PUTS) && positionData?.put?.quantity ?
                        <NonInteractiveTooltip title={`${positionData.put.type === 'buy' ? 'Buy' : 'Sell'} ${positionData.put.quantity}`}>
                            <WorkOutlineRounded sx={{ fontSize: '12px', display: 'block', color: theme => positionData.put.type === 'buy' ? theme.palette.buy.main : theme.palette.sell.main }} />
                        </NonInteractiveTooltip> : null


                }
                {(selectedOptionChainTableView === OptionChainTableView.CALLS || selectedOptionChainTableView === OptionChainTableView.OTM || selectedOptionChainTableView === OptionChainTableView.ITM) &&
                    positionData?.call?.quantity && (
                        <NonInteractiveTooltip
                            title={`${positionData.call.type === 'buy' ? 'Buy' : 'Sell'} ${positionData.call.quantity}`}
                        >
                            <WorkOutlineRounded
                                sx={{
                                    fontSize: '12px',
                                    display: 'block',
                                    color: theme =>
                                        positionData.call.type === 'buy'
                                            ? theme.palette.buy.main
                                            : theme.palette.sell.main,
                                }}
                            />
                        </NonInteractiveTooltip>
                    )}
            </Box>
            <span className="one-sd-tag absolute z-9" style={{ top: -13, left: sdTitleLeft }}>1SD</span>
            <span className="minus-one-sd-tag absolute z-9" style={{ top: -13, left: sdTitleLeft }}>-1SD</span>
            <span className="two-sd-tag absolute z-9" style={{ top: -13, left: sdTitleLeft }}>2SD</span>
            <span className="minus-two-sd-tag absolute z-9" style={{ top: -13, left: sdTitleLeft }}>-2SD</span>
        </Box>
    )
}

export default memo(CustomRenderStrike)
